import React from 'react';
import SearchableSelect from './SearchableSelect';

function HistoricalImportModal({
  show,
  modalRef,
  onClose,
  onChooseFile,
  onImport,
  fileName,
  importPreview,
  fallbackCategoryId,
  onFallbackCategoryChange,
  categoryOptions,
  importing,
}) {
  if (!show) {
    return null;
  }

  const previewRows = importPreview?.rows || [];
  const summary = importPreview?.summary || {
    totalRows: 0,
    validRows: 0,
    invalidRows: 0,
    unmatchedCategoryRows: 0,
  };
  const needsFallbackCategory = summary.unmatchedCategoryRows > 0 && !fallbackCategoryId;
  const canImport = summary.totalRows > 0 && summary.invalidRows === 0 && !needsFallbackCategory;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
      <div
        ref={modalRef}
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
      >
        <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Import Historical Transactions</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-600">
                Upload a spreadsheet export as CSV or TSV. Apple Numbers is supported after exporting the sheet as CSV.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onChooseFile}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Choose File
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
          <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
            Required columns: date, storeName or merchant, and amount.
            Optional columns: itemName, category, paymentMethod, taxAmount, quantity, unitPrice, and storeLocation.
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 sm:px-6">
          <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <div className="space-y-4">
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">File</p>
                <p className="mt-2 text-sm font-medium text-gray-900">{fileName || 'No file selected yet'}</p>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Summary</p>
                <div className="mt-3 space-y-2 text-sm text-gray-700">
                  <div className="flex items-center justify-between gap-3">
                    <span>Total rows</span>
                    <span className="font-semibold text-gray-900">{summary.totalRows}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>Ready to import</span>
                    <span className="font-semibold text-emerald-700">{summary.validRows}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>Rows with errors</span>
                    <span className="font-semibold text-red-700">{summary.invalidRows}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span>Rows needing fallback category</span>
                    <span className="font-semibold text-amber-700">{summary.unmatchedCategoryRows}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <label className="mb-2 block text-sm font-medium text-gray-900">
                  Default category for unmatched rows
                </label>
                <SearchableSelect
                  value={fallbackCategoryId}
                  onChange={onFallbackCategoryChange}
                  options={categoryOptions}
                  placeholder="Choose a fallback category"
                  ariaLabel="Choose fallback category for imported transactions"
                  inputClassName="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Use this when the spreadsheet category text does not match an existing app category.
                </p>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="border-b border-gray-200 px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">Preview</p>
                <p className="mt-1 text-xs text-gray-500">Showing the first 12 parsed rows before import.</p>
              </div>

              {previewRows.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-500">
                  Choose a file to preview the import.
                </div>
              ) : (
                <div className="overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Row</th>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Store</th>
                        <th className="px-3 py-2">Item</th>
                        <th className="px-3 py-2">Amount</th>
                        <th className="px-3 py-2">Category</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {previewRows.slice(0, 12).map((row) => (
                        <tr key={`historical-import-row-${row.rowNumber}`} className="align-top">
                          <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                          <td className="px-3 py-2 text-gray-700">{row.transactionDate || '-'}</td>
                          <td className="px-3 py-2 text-gray-900">{row.storeName || '-'}</td>
                          <td className="px-3 py-2 text-gray-700">{row.itemName || '-'}</td>
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {typeof row.itemTotal === 'number' ? row.itemTotal.toFixed(2) : '-'}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {row.categoryLabel || row.rawCategory || (fallbackCategoryId ? 'Will use fallback' : '-')}
                          </td>
                          <td className="px-3 py-2">
                            {row.errors.length > 0 ? (
                              <span className="text-xs font-medium text-red-700">{row.errors.join(', ')}</span>
                            ) : row.requiresCategoryFallback && !fallbackCategoryId ? (
                              <span className="text-xs font-medium text-amber-700">Needs fallback category</span>
                            ) : (
                              <span className="text-xs font-medium text-emerald-700">Ready</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4 sm:px-6">
          <p className="text-sm text-gray-500">
            {needsFallbackCategory
              ? 'Choose a fallback category for unmatched rows before importing.'
              : canImport
              ? 'Review the preview, then import the valid rows.'
              : 'Fix the file headers or row errors before importing.'}
          </p>
          <button
            type="button"
            onClick={onImport}
            disabled={!canImport || importing}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400"
          >
            {importing ? 'Importing...' : `Import ${summary.validRows} row${summary.validRows === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default HistoricalImportModal;
